import { normalizeAccountStatus, type AccountPlanId, type AccountStatus } from "./accountModels";

const trimEnv = (value: string | undefined): string => value?.trim() ?? "";

const accountApiBaseUrl = (
  trimEnv(import.meta.env.VITE_ACCOUNT_API_BASE_URL) ||
  "https://sehaj-checkout-api.flowcloud.workers.dev"
).replace(/\/$/, "");

type NavigationResponse = {
  navigationUrl?: unknown;
};

const trustedNavigationDomains = ["polar.sh", "stripe.com"] as const;

const isTrustedNavigationHost = (hostname: string): boolean => {
  const normalized = hostname.toLowerCase();
  return trustedNavigationDomains.some(
    (domain) => normalized === domain || normalized.endsWith(`.${domain}`)
  );
};

const parseNavigationUrl = (value: unknown): string => {
  if (typeof value !== "string") {
    throw new Error("The secure destination was not returned.");
  }

  const url = new URL(value);
  if (url.protocol !== "https:" || !isTrustedNavigationHost(url.hostname)) {
    throw new Error("The secure destination was invalid.");
  }
  return url.toString();
};

const readJson = async (response: Response): Promise<unknown> => {
  try {
    return await response.json();
  } catch {
    return null;
  }
};

const accountHeaders = (
  token: string,
  options: { idempotencyKey?: string; returnState?: string | null } = {}
): Headers => {
  const headers = new Headers({
    Accept: "application/json",
    Authorization: `Bearer ${token}`
  });
  if (options.idempotencyKey) {
    headers.set("Idempotency-Key", options.idempotencyKey);
  }
  if (options.returnState) {
    headers.set("X-Sehaj-Return-State", options.returnState);
  }
  return headers;
};

const requestError = (response: Response): Error => {
  if (response.status === 401) return new Error("Your session has expired. Please sign in again.");
  if (response.status === 429) return new Error("Too many attempts. Please wait a moment and try again.");
  if (response.status >= 500) return new Error("This service is temporarily unavailable. Please try again shortly.");
  return new Error("We could not complete that request. Please try again.");
};

export const fetchAccountStatus = async (
  token: string,
  returnState: string | null = null
): Promise<AccountStatus> => {
  const response = await fetch(`${accountApiBaseUrl}/api/account/status`, {
    method: "GET",
    headers: accountHeaders(token, { returnState }),
    credentials: "omit",
    cache: "no-store"
  });
  const payload = await readJson(response);
  if (!response.ok) throw requestError(response);

  const status = normalizeAccountStatus(payload);
  if (!status) throw new Error("The account response was incomplete.");
  return status;
};

export const createAccountCheckout = async (
  token: string,
  planId: AccountPlanId,
  idempotencyKey: string
): Promise<string> => {
  const headers = accountHeaders(token, { idempotencyKey });
  headers.set("Content-Type", "application/json");
  const response = await fetch(`${accountApiBaseUrl}/api/account/create-checkout`, {
    method: "POST",
    headers,
    credentials: "omit",
    body: JSON.stringify({ planId })
  });
  const payload = await readJson(response) as NavigationResponse | null;
  if (!response.ok) throw requestError(response);
  return parseNavigationUrl(payload?.navigationUrl);
};

export const createAccountPortalSession = async (
  token: string,
  idempotencyKey: string
): Promise<string> => {
  const headers = accountHeaders(token, { idempotencyKey });
  headers.set("Content-Type", "application/json");
  const response = await fetch(`${accountApiBaseUrl}/api/account/create-portal-session`, {
    method: "POST",
    headers,
    credentials: "omit",
    body: "{}"
  });
  const payload = await readJson(response) as NavigationResponse | null;
  if (!response.ok) throw requestError(response);
  return parseNavigationUrl(payload?.navigationUrl);
};
